import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, Clock, Truck, Check, Loader2, ArrowLeft, Send } from 'lucide-react';
import { useSiteSelector } from '@/hooks/useSiteSelector';
import { useCommandeFormules } from '@/hooks/useCommandeFormules';
import { useCommandeGlobaleOptions } from '@/hooks/useCommandeGlobaleOptions';
import { FormulaBadges } from '@/components/FormulaBadges';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { VoiceOrderCompleteData, CompletedWorkItem } from './VoiceOrderQuestionnaire';
import { logger } from '@/lib/logger';
import { fmtTime, fmtVolume } from '@/lib/utils';

interface TruckData {
  number: number;
  volume: number;
  time: string;
  formulaId: string;
  workItemAllocations: Array<{
    workItemId: string;
    workItemKey: string;
    volume: number;
  }>;
}

interface VoiceOrderSummaryProps {
  orderData: VoiceOrderCompleteData;
  siteId: string;
  onBack: () => void;
  onSuccess: () => void;
}

export const VoiceOrderSummary: React.FC<VoiceOrderSummaryProps> = ({
  orderData,
  siteId,
  onBack,
  onSuccess,
}) => {
  const { selectedSite } = useSiteSelector();
  const { formules } = useCommandeFormules(siteId);
  const { options: globalOptions } = useCommandeGlobaleOptions(siteId);
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [supplierInfo, setSupplierInfo] = useState<any>(null);
  const [selectedPlant, setSelectedPlant] = useState<any>(null);

  // Load supplier info
  useEffect(() => {
    const loadSupplierInfo = async () => {
      if (!siteId) return;
      
      const { data: globalOrder } = await supabase
        .from('global_orders')
        .select('supplier_id, suppliers(name)')
        .eq('site_id', siteId)
        .maybeSingle();
      
      if (globalOrder?.supplier_id) {
        setSupplierInfo((globalOrder as any).suppliers);
        
        // Get first available plant
        const { data: plants } = await supabase
          .from('concrete_plants')
          .select('id, city, address, email')
          .eq('supplier_id', globalOrder.supplier_id)
          .limit(1);
        
        if (plants && plants.length > 0) {
          setSelectedPlant(plants[0]);
        }
      }
    };
    
    loadSupplierInfo();
  }, [siteId]);

  // Generate trucks from work items (7.5m³ max per truck)
  const trucks = useMemo(() => {
    const MAX_VOLUME = 7.5;
    const generatedTrucks: TruckData[] = [];

    // Group work items by formula
    const workItemsByFormula = orderData.workItems.reduce((acc, workItem) => {
      const formulaId = workItem.formulaId;
      if (!acc[formulaId]) acc[formulaId] = [];
      acc[formulaId].push(workItem);
      return acc;
    }, {} as Record<string, CompletedWorkItem[]>);

    let truckCounter = 1;

    // For each formula, generate trucks with sequential filling
    Object.entries(workItemsByFormula).forEach(([formulaId, workItems]) => {
      // Sort by time
      const sorted = [...workItems].sort((a, b) =>
        a.desiredTime.localeCompare(b.desiredTime)
      );

      let currentTruck: TruckData | null = null;
      let remainingVolumes = sorted.map(o => ({ ...o, remaining: o.volume }));

      while (remainingVolumes.some(o => o.remaining > 0)) {
        if (!currentTruck) {
          const firstWithVolume = remainingVolumes.find(o => o.remaining > 0);
          currentTruck = {
            number: truckCounter++,
            volume: 0,
            time: firstWithVolume?.desiredTime || '08:00',
            formulaId: formulaId,
            workItemAllocations: [],
          };
        }

        const spaceLeft = MAX_VOLUME - currentTruck.volume;
        if (spaceLeft <= 0) {
          generatedTrucks.push(currentTruck);
          currentTruck = null;
          continue;
        }

        // Find first work item with remaining volume
        const itemIdx = remainingVolumes.findIndex(o => o.remaining > 0);
        if (itemIdx === -1) break;

        const item = remainingVolumes[itemIdx];
        const volumeToAllocate = Math.min(spaceLeft, item.remaining);

        currentTruck.workItemAllocations.push({
          workItemId: item.id,
          workItemKey: `${item.building}-${item.level}-${item.name}`,
          volume: volumeToAllocate,
        });
        currentTruck.volume += volumeToAllocate;
        remainingVolumes[itemIdx].remaining -= volumeToAllocate;
      }

      if (currentTruck && currentTruck.volume > 0) {
        generatedTrucks.push(currentTruck);
      }
    });

    return generatedTrucks;
  }, [orderData.workItems]);

  const totalVolume = orderData.workItems.reduce((sum, o) => sum + o.volume, 0);

  const handleSubmit = async () => {
    if (!selectedSite || !user) return;

    setLoading(true);
    try {
      // Get supplier from global order
      const { data: globalOrder } = await supabase
        .from('global_orders')
        .select('supplier_id')
        .eq('site_id', siteId)
        .maybeSingle();

      if (!globalOrder?.supplier_id) {
        throw new Error('Aucune commande globale trouvée');
      }

      // Create concrete_pour with status 'scheduled' first (NOT 'order_sent')
      // We'll update to 'order_sent' AFTER trucks are created to ensure email has complete data
      const { data: betonnage, error: betonnageError } = await supabase
        .from('concrete_pours')
        .insert({
          site_id: siteId,
          site_manager_id: user.id,
          order_date: new Date().toISOString().split('T')[0],
          delivery_date: orderData.date,
          desired_time: orderData.workItems[0]?.desiredTime || '08:00',
          ordered_volume: totalVolume,
          priority: 'normale',
          comment: orderData.comment || 'Commande vocale',
          formula_id: orderData.workItems[0]?.formulaId,
          supplier_id: globalOrder.supplier_id,
          plant_id: selectedPlant?.id || null,
          status: 'scheduled', // Don't trigger email yet - trucks not created
        })
        .select()
        .single();

      if (betonnageError) throw betonnageError;

      // Create or find work_items and insert pour_work_items
      const workItemsMap = new Map<string, string>();

      for (const workItem of orderData.workItems) {
        const key = `${workItem.building}-${workItem.level}-${workItem.name}`;

        const { data: existing } = await supabase
          .from('work_items')
          .select('id')
          .eq('site_id', siteId)
          .eq('building', workItem.building)
          .eq('level', workItem.level)
          .eq('name', workItem.name)
          .maybeSingle();

        if (existing) {
          workItemsMap.set(key, existing.id);
        } else {
          const { data: newItem } = await supabase
            .from('work_items')
            .insert({
              site_id: siteId,
              building: workItem.building,
              level: workItem.level,
              name: workItem.name,
              volume: workItem.volume,
            })
            .select('id')
            .single();

          if (newItem) workItemsMap.set(key, newItem.id);
        }
      }

      // Insert pour_work_items
      const pourWorkItems = orderData.workItems.map(o => ({
        concrete_pour_id: betonnage.id,
        work_item_id: workItemsMap.get(`${o.building}-${o.level}-${o.name}`),
        volume_poured: o.volume,
        formula_id: o.formulaId,
        desired_time: o.desiredTime,
      }));

      await supabase.from('pour_work_items').insert(pourWorkItems);

      // Create concrete_deliveries
      const deliveryInserts = trucks.map(t => ({
        concrete_pour_id: betonnage.id,
        truck_number: t.number,
        ordered_volume: t.volume,
        scheduled_time: t.time,
        formula_id: t.formulaId,
        status: 'scheduled',
      }));

      const { data: deliveries } = await supabase
        .from('concrete_deliveries')
        .insert(deliveryInserts)
        .select();

      // Create delivery_work_items
      if (deliveries) {
        const dwiInserts: any[] = [];
        trucks.forEach((truck, idx) => {
          const delivery = deliveries[idx];
          truck.workItemAllocations.forEach(alloc => {
            const workItemId = workItemsMap.get(alloc.workItemKey);
            if (workItemId) {
              dwiInserts.push({
                delivery_id: delivery.id,
                work_item_id: workItemId,
                volume_delivered: alloc.volume,
              });
            }
          });
        });
        
        if (dwiInserts.length > 0) {
          await supabase.from('delivery_work_items').insert(dwiInserts);
        }
      }

      // Add mandatory options (transport, taxes)
      if (deliveries) {
        const optionInserts: any[] = [];
        const transportOpt = globalOptions.find(o => o.category === 'transport' && o.active);
        const taxesOpts = globalOptions.filter(o => o.category === 'taxes' && o.active);

        deliveries.forEach(d => {
          if (transportOpt) {
            optionInserts.push({
              delivery_id: d.id,
              name: transportOpt.name,
              category: 'transport',
              unit_type: transportOpt.unit_type,
              price: transportOpt.price,
              active: true,
            });
          }
          taxesOpts.forEach(tax => {
            optionInserts.push({
              delivery_id: d.id,
              name: tax.name,
              category: 'taxes',
              unit_type: tax.unit_type,
              price: tax.price,
              active: true,
            });
          });
          
          // Add selected options from voice (PUMI, etc.)
          orderData.options.forEach(optName => {
            const opt = globalOptions.find(o => o.name === optName && o.active);
            if (opt) {
              optionInserts.push({
                delivery_id: d.id,
                name: opt.name,
                category: opt.category,
                unit_type: opt.unit_type,
                price: opt.price,
                active: true,
              });
            }
          });
        });

        if (optionInserts.length > 0) {
          await supabase.from('concrete_delivery_options').insert(optionInserts);
        }
      }

      // NOW update status to 'order_sent' - trucks are now created
      const { error: statusUpdateError } = await supabase
        .from('concrete_pours')
        .update({ status: 'order_sent' })
        .eq('id', betonnage.id);

      if (statusUpdateError) throw statusUpdateError;

      // Short delay so DB write is visible to Edge Function before it reads
      await new Promise((r) => setTimeout(r, 500));
      // Explicitly call send-order-notification (no trigger anymore)
      const { error: notifError } = await supabase.functions.invoke('send-order-notification', {
        body: { orderId: betonnage.id }
      });
      
      if (notifError) {
        logger.error("VoiceOrderSummary", "Error sending order notification:", notifError);
      } else {
      }

      if (!notifError) {
        toast.success('Commande créée', { description: 'La commande a été envoyée au centralier',
        });
      } else {
        toast.error('Commande créée', { description: "L'envoi du mail a échoué. Vous pouvez renvoyer le mail depuis le détail du bétonnage."});
      }

      onSuccess();
    } catch (err: any) {
      toast.error('Erreur', { description: err.message || 'Erreur lors de la création'});
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-xl w-full max-w-lg">
      <div className="text-center mb-lg">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-lg">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-title">Récapitulatif</h3>
        <p className="text-body-md text-muted-foreground">
          Vérifiez les informations avant l'envoi
        </p>
      </div>

      {/* Résumé */}
      <Card>
        <CardHeader>
          <CardTitle className="text-label">Résumé du bétonnage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-md">
          <div className="grid grid-cols-2 gap-lg text-body-md">
            <div>
              <span className="text-muted-foreground">Chantier</span>
              <p className="font-medium">{selectedSite?.name}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Date</span>
              <p className="font-medium">{new Date(orderData.date).toLocaleDateString('fr-FR')}</p>
            </div>
          </div>
          
          {supplierInfo && (
            <div className="text-body-md border-t pt-md">
              <span className="text-muted-foreground">Fournisseur</span>
              <p className="font-medium">{supplierInfo.name}</p>
              {selectedPlant && (
                <p className="text-helper text-muted-foreground">Centrale {selectedPlant.city}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ouvrages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-label flex items-center gap-sm">
            <Building className="w-4 h-4" />
            Ouvrages ({orderData.workItems.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-sm">
          {orderData.workItems.map(workItem => {
            const formule = formules.find(f => f.id === workItem.formulaId);
            return (
              <div key={workItem.id} className="p-md bg-muted/50 rounded-lg">
                <div className="flex items-center gap-sm mb-1">
                  <span className="text-body-md">{workItem.building} - {workItem.level}</span>
                </div>
                <div className="flex flex-wrap items-center gap-sm text-body-md">
                  <span className="text-muted-foreground">{workItem.name}</span>
                  <Badge variant="outline">{fmtVolume(workItem.volume)} m³</Badge>
                  <Badge variant="time">{fmtTime(workItem.desiredTime)}</Badge>
                  {formule && (
                    <FormulaBadges
                      name={formule.name}
                      strength={formule.resistance}
                      exposureClass={formule.classe_exposition}
                      consistency={formule.consistance}
                      granulometry={formule.granulometrie}
                      size="sm"
                    />
                  )}
                </div>
              </div>
            );
          })}
          
          <div className="pt-2 border-t flex justify-between items-center">
            <span className="text-label">Volume total</span>
            <Badge>{fmtVolume(totalVolume)} m³</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Camions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-label flex items-center gap-sm">
            <Truck className="w-4 h-4" />
            Planning des camions ({trucks.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-sm">
          {trucks.map(truck => {
            const formule = formules.find(f => f.id === truck.formulaId);
            return (
              <div key={truck.number} className="p-md bg-muted/50 rounded-lg">
                <div className="flex items-center gap-sm flex-wrap">
                  <Badge variant="outline">Camion {truck.number}</Badge>
                  <span className="text-body-md">{fmtVolume(truck.volume)} m³</span>
                  <Badge variant="time">{fmtTime(truck.time)}</Badge>
                  {formule && (
                    <Badge variant="secondary" className="text-body-sm">{formule.name}</Badge>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Options */}
      {orderData.options.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-label">Options sélectionnées</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-sm">
              {orderData.options.map(opt => (
                <Badge key={opt} variant="secondary">{opt}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-md">
        <Button variant="outline" onClick={onBack} className="flex-1 gap-sm">
          <ArrowLeft className="w-4 h-4" />
          Modifier
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={loading}
          className="flex-1 gap-sm"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Envoi...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Confirmer et envoyer
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
